import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Cronometro3030 from './Cronometro3030';

const SENSACAO = [
  { value: 'facil', label: { pt: 'Fácil', en: 'Easy' }, emoji: '😊' },
  { value: 'ok', label: { pt: 'Ok', en: 'Ok' }, emoji: '😐' },
  { value: 'dificil', label: { pt: 'Difícil', en: 'Hard' }, emoji: '😓' },
];

function getYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
}

function beep(freq = 880, duration = 120, vol = 0.15) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.frequency.value = freq; osc.type = 'sine';
    gain.gain.setValueAtTime(vol, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration / 1000);
    osc.start(ctx.currentTime); osc.stop(ctx.currentTime + duration / 1000);
  } catch (e) {}
}

// Timer de descanso entre séries (modo reps)
function TimerDescanso({ descansaSeg, onFim, lang, pt }) {
  const [seg, setSeg] = useState(descansaSeg || 30);
  const ref = useRef(null);
  const inicioRef = useRef(Date.now());

  useEffect(() => {
    inicioRef.current = Date.now();
    ref.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - inicioRef.current) / 1000);
      const restante = (descansaSeg || 30) - elapsed;
      if (restante <= 0) {
        clearInterval(ref.current);
        beep(660, 100, 0.12);
        beep(880, 120, 0.15);
        if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
        onFim();
      } else {
        if (restante === 3) beep(330, 80, 0.08);
        setSeg(restante);
      }
    }, 500);
    return () => clearInterval(ref.current);
  }, []);

  const pct = ((( descansaSeg || 30) - seg) / (descansaSeg || 30)) * 100;
  const raio = 36;
  const circ = 2 * Math.PI * raio;
  const dashOffset = circ - (pct / 100) * circ;

  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', padding:'16px', background:'rgba(24,101,174,0.06)', border:'1.5px solid rgba(24,101,174,0.2)', borderRadius:20, marginTop:10 }}>
      <div style={{ fontSize:11, fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:'#18659E', marginBottom:10 }}>
        {pt ? 'Descansando...' : 'Rest...'}
      </div>
      <div style={{ position:'relative', width:88, height:88 }}>
        <svg width="88" height="88" style={{ transform:'rotate(-90deg)' }}>
          <circle cx="44" cy="44" r={raio} fill="none" stroke="rgba(37,28,25,0.08)" strokeWidth="5" />
          <circle cx="44" cy="44" r={raio} fill="none" stroke="#18659E" strokeWidth="5"
            strokeDasharray={circ} strokeDashoffset={dashOffset} strokeLinecap="round"
            style={{ transition:'stroke-dashoffset 0.9s linear' }} />
        </svg>
        <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'Inter',sans-serif", fontSize:26, fontWeight:700, color:'#18659E' }}>
          {seg}
        </div>
      </div>
      <button onClick={onFim} style={{ marginTop:10, padding:'8px 20px', background:'#18659E', color:'white', border:'none', borderRadius:14, fontSize:12, fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase', cursor:'pointer', fontFamily:"'Inter',sans-serif" }}>
        {pt ? 'Pular descanso →' : 'Skip rest →'}
      </button>
    </div>
  );
}

export default function ExercicioItem({ ex, checkinAtivo, exState, onUpdate, onConcluir, lang, ultimaCarga, semDescansoEntre }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  // Navegação por série (modo reps)
  const [serieAtual, setSerieAtual] = useState(1);
  const [descansando, setDescansando] = useState(false);
  const videoId = getYouTubeId(ex.video);
  const concluido = exState?.concluido || false;
  const pulado = exState?.pulado || false;
  const pt = lang === 'pt';
  const totalSeries = parseInt(ex.series) || 4;

  useEffect(() => {
    if (!checkinAtivo) return;
    setSerieAtual(Math.min(totalSeries, Math.max(1, Number(exState?.serieAtual) || 1)));
    setDescansando(false);
  }, [checkinAtivo, ex.id, totalSeries]);

  const isTempo = ex.modo === 'tempo';
  const repsLimpa = String(ex.reps || '').replace(/^\s*\d+\s*x\s*/i, '').trim();
  const seriesLabel = isTempo
    ? `${ex.series || 4}x ${ex.execucaoSeg || 30}seg / ${ex.descansaSeg || 30}seg descanso`
    : `${ex.series || 4} ${pt ? 'séries' : 'sets'}${repsLimpa ? ` · ${repsLimpa}` : ''}${ex.descansaSeg ? ` · ${ex.descansaSeg}seg ${pt ? 'de descanso' : 'rest'}` : ''}`;

  function concluirSerie() {
    if (serieAtual >= totalSeries) {
      // Última série — abre feedback
      onUpdate('marcando', true);
      beep(440, 200, 0.2);
      if (navigator.vibrate) navigator.vibrate([200, 100, 400]);
    } else {
      if (semDescansoEntre) {
        beep(520, 100, 0.12);
        const proxima = serieAtual + 1;
        setSerieAtual(proxima);
        onUpdate('serieAtual', proxima);
        return;
      }
      // Inicia descanso
      beep(440, 150, 0.15);
      if (navigator.vibrate) navigator.vibrate([200, 100, 100]);
      setDescansando(true);
    }
  }

  function proximaSerie() {
    setDescansando(false);
    const proxima = serieAtual + 1;
    setSerieAtual(proxima);
    onUpdate('serieAtual', proxima);
  }

  return (
    <div style={{
      border: `1px solid ${concluido ? 'rgba(94,104,64,0.22)' : pulado ? 'rgba(37,28,25,0.08)' : expanded ? 'rgba(137,91,64,0.22)' : 'rgba(255,255,255,.76)'}`,
      borderRadius: 24, marginBottom: 10,
      background: concluido ? 'rgba(217,221,193,0.34)' : pulado ? 'rgba(37,28,25,0.02)' : 'rgba(255,255,255,.82)',
      overflow: 'hidden', transition: 'all 0.2s',
      boxShadow: expanded ? '0 16px 34px rgba(82,69,57,.08), inset 0 1px 0 rgba(255,255,255,.86)' : '0 10px 24px rgba(82,69,57,.045), inset 0 1px 0 rgba(255,255,255,.84)',
      opacity: pulado ? 0.6 : 1,
    }}>
      {/* Cabeçalho */}
      <div
        onClick={checkinAtivo && !concluido && !pulado ? () => setExpanded(e => !e) : undefined}
        style={{ padding: '15px 16px', display: 'flex', alignItems: 'center', gap: 12, cursor: checkinAtivo && !concluido && !pulado ? 'pointer' : 'default' }}
      >
        <div style={{
          width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
          background: concluido ? 'var(--accent)' : pulado ? 'rgba(37,28,25,0.06)' : 'transparent',
          border: `1.5px solid ${concluido ? 'var(--accent)' : pulado ? 'rgba(37,28,25,0.16)' : 'rgba(216,202,182,.86)'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s',
        }}>
          {concluido && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#FFF6ED" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
          {pulado && <span style={{ fontSize:'10px', color:'var(--text-2)', fontWeight:700 }}>–</span>}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 650, color: concluido ? '#5e6840' : 'var(--text)', marginBottom: 3, lineHeight: 1.25 }}>{ex.nome}</div>
          <div style={{ fontSize: 11, color: 'var(--text-2)', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', lineHeight:1.35 }}>
            <span>{seriesLabel}</span>
            {ex.carga && <span>· {ex.carga}</span>}
            {(ex.posicaoPes && ex.posicaoPes !== '—') && <span>· {ex.posicaoPes}</span>}
            {(ex.posicaoMaos && ex.posicaoMaos !== '—') && <span>· {ex.posicaoMaos}</span>}
            {ultimaCarga && !concluido && (
              <span style={{ background:'rgba(137,91,64,0.08)', color:'var(--accent)', padding:'2px 7px', borderRadius:14, fontSize:10, fontWeight:600, lineHeight:1.25 }}>↩ {ultimaCarga}</span>
            )}
          </div>
        </div>
        {concluido
          ? <span style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 600 }}>✓ {pt ? 'Feito' : 'Done'}</span>
          : pulado
          ? <span style={{ fontSize: 11, color: 'var(--text-2)', fontStyle:'italic' }}>{pt ? 'pulado' : 'skipped'}</span>
          : checkinAtivo && !isTempo
            ? <div style={{ display:'flex', gap:4, alignItems:'center' }}>
                <span style={{ fontSize:11, color:'var(--accent)', fontWeight:700 }}>{pt ? `Série ${serieAtual} de ${totalSeries}` : `Set ${serieAtual} of ${totalSeries}`}</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-2)" strokeWidth="1.5" strokeLinecap="round"><polyline points={expanded ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} /></svg>
              </div>
            : checkinAtivo
              ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-2)" strokeWidth="1.5" strokeLinecap="round"><polyline points={expanded ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} /></svg>
              : null
        }
      </div>

      {/* Expandido */}
      {expanded && !concluido && !pulado && checkinAtivo && (
        <div style={{ padding: '0 16px 16px', borderTop: '1px solid var(--divider)' }}>

          {ex.obs && (
            <div style={{ padding: '10px 12px', background: 'rgba(137,91,64,0.06)', borderLeft: '2px solid var(--accent)', borderRadius: '0 6px 6px 0', fontSize: 12, color: 'var(--text-3)', marginTop: 12, marginBottom: 12, lineHeight: 1.6 }}>
              <strong style={{ color: 'var(--accent)' }}>{pt ? 'Carol diz: ' : 'Carol says: '}</strong>{ex.obs}
            </div>
          )}

          {((ex.posicaoPes && ex.posicaoPes !== '—') || (ex.posicaoMaos && ex.posicaoMaos !== '—')) && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              {ex.posicaoPes && ex.posicaoPes !== '—' && <div style={{ padding: '5px 10px', background: 'var(--surface-2)', borderRadius: 14, fontSize: 11, color: 'var(--text-3)' }}>👣 {ex.posicaoPes}</div>}
              {ex.posicaoMaos && ex.posicaoMaos !== '—' && <div style={{ padding: '5px 10px', background: 'var(--surface-2)', borderRadius: 14, fontSize: 11, color: 'var(--text-3)' }}>🤝 {ex.posicaoMaos}</div>}
            </div>
          )}

          {videoId && (
            <div style={{ marginBottom: 12 }}>
              {showVideo ? (
                <div>
                  <iframe width="100%" height="200" src={`https://www.youtube.com/embed/${videoId}`} title={ex.nome} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen style={{ borderRadius: 14, display: 'block' }} />
                  <button onClick={() => setShowVideo(false)} style={{ marginTop: 6, background: 'transparent', border: 'none', color: 'var(--text-2)', fontSize: 12, cursor: 'pointer', padding: 0 }}>✕ {pt ? 'Fechar' : 'Close'}</button>
                </div>
              ) : (
                <button onClick={() => setShowVideo(true)} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '8px 14px', background: 'transparent', border: '1.5px solid var(--divider)', borderRadius: 14, fontSize: 12, fontWeight: 600, color: 'var(--text)', cursor: 'pointer', fontFamily: "'Inter',sans-serif" }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><polygon points="5 3 19 12 5 21 5 3" /></svg>
                  {pt ? 'Ver demonstração' : 'Watch demo'}
                </button>
              )}
            </div>
          )}

          {!videoId && (
            <div style={{ marginBottom:12, padding:'9px 11px', borderRadius:14, background:'rgba(137,91,64,.06)', color:'var(--text-2)', fontSize:11, lineHeight:1.45 }}>
              {pt ? 'Este exercício ainda não tem vídeo. Se não tiver certeza da execução, peça uma demonstração à equipe antes de continuar.' : 'This exercise does not have a video yet. Ask the team for a demonstration if you are unsure.'}
            </div>
          )}

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:7, marginBottom:12 }}>
            <button type="button" onClick={() => navigate('/aluna/chat')} style={{ padding:'9px 8px', border:'1px solid rgba(137,91,64,.22)', borderRadius:14, background:'rgba(255,255,255,.6)', color:'var(--accent)', fontSize:11, fontWeight:650, cursor:'pointer' }}>
              {pt ? 'Não consigo fazer' : "I can't do this"}
            </button>
            <button type="button" onClick={() => { onUpdate('obs', pt ? 'Senti dor ou desconforto neste exercício.' : 'I felt pain or discomfort during this exercise.'); navigate('/aluna/chat'); }} style={{ padding:'9px 8px', border:'1px solid rgba(176,67,55,.2)', borderRadius:14, background:'rgba(176,67,55,.05)', color:'#9b3f35', fontSize:11, fontWeight:650, cursor:'pointer' }}>
              {pt ? 'Senti dor' : 'I felt pain'}
            </button>
          </div>

          {isTempo ? (
            /* MODO TEMPO — Cronômetro 30/30 */
            <div style={{ marginTop: 12 }}>
              <Cronometro3030
                series={ex.series} execucaoSeg={ex.execucaoSeg} descansaSeg={ex.descansaSeg}
                lang={lang} onConcluirSeries={() => onUpdate('marcando', true)}
              />
              {exState?.marcando && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: 10 }}>{pt ? 'Como foi?' : 'How did it go?'}</div>
                  <input style={{ width: '100%', padding: '9px 12px', border: '1.5px solid var(--divider)', borderRadius: 14, fontSize: 13, color: 'var(--text)', background: 'var(--surface-2)', outline: 'none', fontFamily: "'Inter',sans-serif", marginBottom: 8, boxSizing: 'border-box' }} placeholder={pt ? 'Carga (ex: 18kg)' : 'Weight (e.g. 40lbs)'} value={exState?.carga || ''} onChange={e => onUpdate('carga', e.target.value)} />
                  <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                    {SENSACAO.map(op => <button key={op.value} onClick={() => onUpdate('sensacao', op.value)} style={{ flex: 1, padding: '7px 4px', border: `1.5px solid ${exState?.sensacao === op.value ? 'var(--text)' : 'var(--divider)'}`, borderRadius: 14, background: exState?.sensacao === op.value ? 'var(--text)' : 'var(--surface)', color: exState?.sensacao === op.value ? '#FAF7F2' : 'var(--text)', fontSize: 12, cursor: 'pointer', fontFamily: "'Inter',sans-serif" }}>{op.emoji} {op.label[lang] || op.label.pt}</button>)}
                  </div>
                  <textarea style={{ width: '100%', padding: '9px 12px', border: '1.5px solid var(--divider)', borderRadius: 14, fontSize: 13, color: 'var(--text)', background: 'var(--surface-2)', outline: 'none', fontFamily: "'Inter',sans-serif", resize: 'none', height: 56, marginBottom: 10, boxSizing: 'border-box' }} placeholder={pt ? 'Observação (opcional)...' : 'Optional notes...'} value={exState?.obs || ''} onChange={e => onUpdate('obs', e.target.value)} />
                  <button onClick={() => { onConcluir(); setExpanded(false); }} style={{ width: '100%', padding: 10, background: '#2E7D32', color: '#fff', border: 'none', borderRadius: 14, fontSize: 13, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: "'Inter',sans-serif" }}>{pt ? 'Salvar exercício ✓' : 'Save exercise ✓'}</button>
                </div>
              )}
              {!exState?.marcando && (
                <div style={{ display:'flex', flexDirection:'column', gap:6, marginTop:10 }}>
                  <button onClick={() => onUpdate('marcando', true)} style={{ width:'100%', padding:10, background:'var(--text)', color:'#FAF7F2', border:'none', borderRadius:14, fontSize:13, fontWeight:600, letterSpacing:'0.06em', textTransform:'uppercase', cursor:'pointer', fontFamily:"'Inter',sans-serif" }}>{pt?'Marcar como feito':'Mark as done'}</button>
                  <button onClick={() => { onUpdate('pulado', true); onConcluir(); setExpanded(false); }} style={{ width:'100%', padding:'8px', background:'transparent', border:'1px solid rgba(37,28,25,0.12)', borderRadius:14, fontSize:12, color:'var(--text-2)', cursor:'pointer', fontFamily:"'Inter',sans-serif" }}>{pt?'Pular exercício':'Skip exercise'}</button>
                </div>
              )}
            </div>
          ) : (
            /* MODO REPS — Navegação por série */
            <div style={{ marginTop: 12 }}>
              {/* Indicador de séries */}
              {!exState?.marcando && (
                <>
                  <div style={{ display:'flex', gap:6, justifyContent:'center', marginBottom:14 }}>
                    {Array.from({ length: totalSeries }).map((_, i) => {
                      const feita = i < serieAtual - 1;
                      const atual = i === serieAtual - 1;
                      return (
                        <div key={i} style={{
                          width:32, height:32, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center',
                          fontSize:12, fontWeight:700,
                          background: feita ? 'var(--accent)' : 'transparent',
                          border: `1.5px solid ${feita ? 'var(--accent)' : atual ? 'var(--text)' : 'rgba(37,28,25,0.15)'}`,
                          color: feita ? '#fff' : atual ? 'var(--text)' : '#8C7F70',
                          transition:'all 0.3s',
                        }}>
                          {feita ? '✓' : i + 1}
                        </div>
                      );
                    })}
                  </div>

                  <div style={{ textAlign:'center', fontSize:13, color:'var(--text-2)', marginBottom:12 }}>
                    {pt ? `Série ${serieAtual} de ${totalSeries}` : `Set ${serieAtual} of ${totalSeries}`}
                    {repsLimpa && <span style={{ marginLeft:6, fontWeight:600, color:'var(--text)' }}>· {repsLimpa}</span>}
                  </div>

                  {descansando ? (
                    <TimerDescanso descansaSeg={ex.descansaSeg} onFim={proximaSerie} lang={lang} pt={pt} />
                  ) : (
                    <button onClick={concluirSerie} style={{ width:'100%', padding:12, background:'var(--text)', color:'#FAF7F2', border:'none', borderRadius:14, fontSize:13, fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase', cursor:'pointer', fontFamily:"'Inter',sans-serif" }}>
                      {serieAtual >= totalSeries
                        ? (pt ? 'Concluir última série ✓' : 'Complete last set ✓')
                        : (pt ? `Concluir série ${serieAtual} →` : `Complete set ${serieAtual} →`)}
                    </button>
                  )}
                  {semDescansoEntre && !descansando && (
                    <div style={{ marginTop:8, padding:'8px 10px', borderRadius:12, background:'rgba(137,91,64,0.07)', color:'var(--accent)', fontSize:11, fontWeight:650, textAlign:'center' }}>
                      {pt ? 'Sem descanso: siga para o próximo exercício do bi-set.' : 'No rest: continue to the next exercise in the superset.'}
                    </div>
                  )}
                  <button
                    onClick={() => { onUpdate('pulado', true); onConcluir(); setExpanded(false); }}
                    style={{ width:'100%', marginTop:8, padding:'8px', background:'transparent', border:'1px solid rgba(37,28,25,0.12)', borderRadius:14, fontSize:12, color:'var(--text-2)', cursor:'pointer', fontFamily:"'Inter',sans-serif", letterSpacing:'0.04em' }}
                  >
                    {pt ? 'Pular exercício' : 'Skip exercise'}
                  </button>
                </>
              )}

              {exState?.marcando && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: 10 }}>{pt ? 'Como foi?' : 'How did it go?'}</div>
                  <input style={{ width: '100%', padding: '9px 12px', border: '1.5px solid var(--divider)', borderRadius: 14, fontSize: 13, color: 'var(--text)', background: 'var(--surface-2)', outline: 'none', fontFamily: "'Inter',sans-serif", marginBottom: 8, boxSizing: 'border-box' }} placeholder={pt ? 'Carga que você usou (ex: 18kg)' : 'Weight used (e.g. 40lbs)'} value={exState?.carga || ''} onChange={e => onUpdate('carga', e.target.value)} />
                  <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                    {SENSACAO.map(op => <button key={op.value} onClick={() => onUpdate('sensacao', op.value)} style={{ flex: 1, padding: '7px 4px', border: `1.5px solid ${exState?.sensacao === op.value ? 'var(--text)' : 'var(--divider)'}`, borderRadius: 14, background: exState?.sensacao === op.value ? 'var(--text)' : 'var(--surface)', color: exState?.sensacao === op.value ? '#FAF7F2' : 'var(--text)', fontSize: 12, cursor: 'pointer', fontFamily: "'Inter',sans-serif" }}>{op.emoji} {op.label[lang] || op.label.pt}</button>)}
                  </div>
                  <textarea style={{ width: '100%', padding: '9px 12px', border: '1.5px solid var(--divider)', borderRadius: 14, fontSize: 13, color: 'var(--text)', background: 'var(--surface-2)', outline: 'none', fontFamily: "'Inter',sans-serif", resize: 'none', height: 56, marginBottom: 10, boxSizing: 'border-box' }} placeholder={pt ? 'Observação livre (opcional)...' : 'Optional notes...'} value={exState?.obs || ''} onChange={e => onUpdate('obs', e.target.value)} />
                  <button onClick={() => { onConcluir(); setExpanded(false); }} style={{ width: '100%', padding: 10, background: '#2E7D32', color: '#fff', border: 'none', borderRadius: 14, fontSize: 13, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: "'Inter',sans-serif" }}>{pt ? 'Salvar exercício ✓' : 'Save exercise ✓'}</button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
